import adminApiClient from '../../api/base/adminApiClient'

/** 장소 한 곳이 어떻게 끝났는가 */
export type InstagramTestStatus =
  /** 묻지 않고 담을 만큼 확실하다 */
  | 'confirmed'
  /** 찾긴 했는데 어느 것인지 못 정했다 — 사용자가 고른다 */
  | 'ambiguous'
  /** 찾았는데 먹는 곳이 아니었다 (사진관·서점 등) */
  | 'not_dining'
  /** 카카오에 그런 가게가 없다 */
  | 'not_found'

/** 어느 길로 확정했는가 */
export type InstagramTestMatchedBy =
  /** 캡션의 @계정 ↔ 카카오가 들고 있는 홈페이지. 가장 강하다 */
  | 'handle'
  /** 캡션에 적힌 주소 반경 300m */
  | 'address'
  /** 동네 단서 반경 2km */
  | 'region'
  /** 전국에서 찾은 뒤 시·도로 걸러 한 곳 */
  | 'broad_region'
  /** 지역을 모른 채 이름만으로 한 곳 */
  | 'name'

export interface InstagramTestPlace {
  kakao_place_id: string
  name: string
  address: string
  category: string
}

/**
 * 확정이 **아닐 때** 왜 그랬는지.
 *
 * 이유마다 고쳐야 할 자리가 전혀 다르다 — 검색을 고칠 일인지, 규칙을
 * 고칠 일인지, 우리가 못 고치는 일인지.
 */
export type InstagramTestReason =
  /** 주소 반경 300m 안에 여러 곳 */
  | 'many_near_address'
  /** 동네 반경 2km 안에 여러 곳 */
  | 'many_in_region'
  /** 시·도로 거르고도 여러 곳 */
  | 'many_in_broad'
  /** 전국 이름 검색에 여러 곳 */
  | 'many_by_name'
  /** 한 곳만 찾았는데 글의 동네가 아니라 확정을 보류 */
  | 'one_but_other_neighbourhood'
  /** 역·터미널 이름이라 상호로 보지 않음 */
  | 'transit_stop'
  /** 작성자가 괄호에 소품샵·서점이라고 적어둠 */
  | 'caption_label'
  /** 계정은 맞는데 먹는 곳이 아니었음 */
  | 'handle_hit_not_dining'
  /** 카카오 분류가 먹는 곳이 아님 */
  | 'category'
  /** 카카오에 그런 가게가 없음 */
  | 'no_match'
  | 'empty_name'

export interface InstagramTestResult {
  /** 캡션에서 읽어낸 상호 */
  name: string
  /** 그 줄에 붙어 있던 인스타 계정 */
  handle: string | null
  /** 그 줄에 적혀 있던 주소 */
  address: string | null
  /** 'pin' = 📍 같은 표식이 붙은 줄, 'loose' = 그냥 주워 온 줄 */
  kind: string
  status: InstagramTestStatus
  matched_by: InstagramTestMatchedBy | null
  /** 확정이 아닐 때만 채워진다 */
  reason: InstagramTestReason | null
  place: InstagramTestPlace | null
  candidates: InstagramTestPlace[]
}

export interface InstagramTestPost {
  url: string
  ok: boolean
  /** ok 가 false 일 때만 */
  message?: string
  author?: string
  thumbnail?: string | null
  caption?: string
  /** 이 글을 어느 지역으로 읽었나 */
  regions?: {
    /** 동네 단서. 앞에서부터 차례로 시도한다 */
    specific: string[]
    /** 시·도. 전국에서 찾았을 때 이것으로 거른다 */
    broad: string | null
  }
  /**
   * 캡션을 '읽는' 단계가 어떻게 됐나.
   *
   * 결과가 비었을 때 **찾기에서 막힌 건지 읽기에서 막힌 건지**를 가르는 값이다.
   * 둘은 고치는 자리가 전혀 다르다.
   */
  reading?: {
    caption_length: number
    /** 빈 줄을 뺀 줄 수 */
    line_count: number
    /** 📍·번호·핸들 같은 표식으로 읽은 상호 수 */
    marked: number
    /** 표식 없이 마지막 수단으로 주워 온 상호 수 */
    loose: number
  }
  results?: InstagramTestResult[]
  summary?: Record<InstagramTestStatus, number>
}

export const adminInstagramTestApi = {
  /**
   * 링크들을 가져오기 흐름 그대로 돌려본다.
   *
   * **아무것도 저장하지 않는다** — 캡션을 읽고 장소를 확정하는 데까지만 간다.
   * 서버가 한 번에 다섯 개까지만 받는다(카카오 호출이 글당 수십 번이다).
   */
  testInstagramShare: async (
    urls: string[]
  ): Promise<{ posts: InstagramTestPost[] }> => {
    const response = await adminApiClient.post<{ posts: InstagramTestPost[] }>(
      '/admin/instagram/test',
      { urls }
    )
    return response.data
  },
}
